/**
 * 解决方案页数据（批次二 · 工业物联网）
 *
 * 归属业务线：iot
 * 事实来源：hoyotech-iot 模块 + hoyotech-common-iiot 公共模块
 *   - 控制器：IotDeviceInfoController / IotDeviceDataController / IotAlarmRecordController /
 *             IotMonitorRuleController / IotMaintenanceRecordController / IotFiringCycleController /
 *             IotDeviceLogController
 *   - 公共库：IndustrialProtocolParser / ModbusProtocolParser / JsonGenericProtocolParser /
 *             MqttDirectProtocolParser / ProtocolParserFactory / ProtocolType /
 *             MqttClientManager / DataProcessor / IndustrialData / RuleEngine /
 *             TimeseriesDbService / InfluxDbServiceImpl
 *   - 实体：IotDeviceInfo / IotDeviceData / IotDeviceFieldMapping / IotDeviceLog /
 *           IotMonitorRule / IotRuleStage / IotDeviceRuleBinding / IotAlarmRecord / IotAlarmProcess /
 *           IotMaintenanceRecord / IotFiringCycle
 *
 * 写作纪律：不得写「自研大模型」「产量预测」等无依据内容；
 * 无实测数据的性能/准确率数字一律不写。
 */
export const iotSolutions = [
  /* ==================== 工业 · 协议接入 ==================== */
  {
    slug: 'iot-protocol-access',
    line: 'iot',
    name: '工业协议接入与设备网关',
    en: 'Protocol Access',
    eyebrow: 'Protocol Access',
    tagline: '先让设备说上话，再谈数据能用',
    summary:
      '协议解析工厂 + Modbus / JSON 通用 / MQTT 直连三类解析器 + 字段映射。' +
      '新增一种设备，补的是解析器与字段映射，不是改平台主干。',
    overview:
      '工业现场的协议比农业更杂：老设备是 Modbus RTU/TCP，新设备爱走 MQTT，' +
      '还有一些厂商用自定义的 JSON 报文。如果每接一类设备都要动平台代码，' +
      '项目就会被协议适配拖住。这套实现把「协议」做成可注册的解析器，' +
      '把「字段」做成可配置的映射，接入新设备主要是配置工作量。',
    pains: [
      { title: '接一台新设备就要改一次代码', desc: '协议散落在业务逻辑里，每次适配都要开发介入、回归测试，现场等不起。' },
      { title: '报文读得懂，但字段对不上', desc: '设备报的是寄存器地址和原始值，业务要的是温度、压力；中间缺一层可配置的映射。' },
      { title: '连不上时说不清断在哪', desc: '是网络、是连接鉴权、还是报文格式变了？没有分层日志就只能靠猜。' }
    ],
    capabilities: [
      { icon: 'link', title: '协议解析工厂', desc: '按协议类型注册并取用对应解析器，新增协议以插件方式接入。', tags: ['工厂模式', '协议可注册', '不改主干'] },
      { icon: 'cpu', title: 'Modbus 解析', desc: '面向 PLC、仪表等常见工业设备，按寄存器与数据类型解析原始报文。', tags: ['Modbus', '寄存器解析', '老设备兼容'] },
      { icon: 'code', title: 'JSON 通用解析', desc: '面向自定义 JSON 报文的设备，用通用解析器加字段映射即可接入。', tags: ['通用解析', '自定义报文', '配置化'] },
      { icon: 'wifi', title: 'MQTT 直连', desc: '设备或网关直接以 MQTT 上报，由连接管理器统一维护会话。', tags: ['MQTT 直连', '连接管理', '主题订阅'] },
      { icon: 'layers', title: '字段映射', desc: '把设备原始字段映射到业务量，含单位与量程，避免把换算写进代码。', tags: ['字段映射', '单位量程', '可配置'] },
      { icon: 'file', title: '设备日志与数据管道', desc: '原始数据经统一管道处理并留存日志，排查时有据可依。', tags: ['数据管道', '设备日志', '可追溯'] }
    ],
    how: [
      { title: '盘点设备与协议', desc: '列出要接入的设备型号、通信方式与协议类型，识别需要新写解析器的部分。' },
      { title: '确定接入路径', desc: '能直连的走 MQTT 直连，老设备走 Modbus 或加网关转换，避免为单台设备定制。' },
      { title: '配置字段映射', desc: '逐字段核对原始值与业务量、单位与量程，先在少量设备上验证。' },
      { title: '验证数据链路', desc: '连续观察一段时间，确认无丢点、无异常跳变，再批量铺开。' },
      { title: '纳入监控', desc: '把设备接入状态与数据质量纳入监控规则，异常时能第一时间发现。' }
    ],
    specs: [
      { k: '解析器', v: '按协议类型注册的解析器工厂，内置 Modbus、JSON 通用与 MQTT 直连三类' },
      { k: '扩展方式', v: '新增协议以解析器实现接入，不改动业务主干' },
      { k: '字段映射', v: '设备原始字段到业务量独立映射，含单位与量程配置' },
      { k: '连接管理', v: 'MQTT 连接由连接管理器统一维护，避免业务代码各自建连' },
      { k: '数据管道', v: '原始数据经统一处理管道入库，并留存设备日志' },
      { k: '现场确认项', v: '具体设备型号、通信接口与点表需现场勘查后确认' }
    ],
    faq: [
      { q: '我们的设备是十多年前的，还能接吗？', a: '多数可以。老设备通常支持 Modbus RTU/TCP，属于已内置的解析类型；如果只有专有协议或串口输出，一般通过加装采集模块或网关转换来接入，通常不需要停机改造。具体型号需现场确认。' },
      { q: '接一台新设备要多久？', a: '取决于协议是否已支持。落在 Modbus / JSON / MQTT 三类之内的，主要是点表核对与字段映射配置；确实是全新专有协议的，需要增加一个解析器实现。这个判断要在看到设备手册和点表后才能给准。' },
      { q: '为什么要把字段映射单独拿出来？', a: '因为设备和业务的命名几乎不会一致，而且单位、量程经常需要换算。把映射放在配置里，现场量程变了或换了一批同型号设备，改配置就行，不必改代码、不必重新走一遍发版。' },
      { q: '数据上来了，但怀疑有丢点怎么办？', a: '按三层看：设备侧的采集周期与通信质量、平台侧的连接状态与设备日志、以及入库后的数据连续性。这套实现里设备连接状态与原始数据日志都留有记录，可以把问题定位到具体一层，而不是笼统地怀疑网络。' }
    ],
    related: ['iot-line-monitoring', 'device-access']
  },

  /* ==================== 工业 · 状态监测 ==================== */
  {
    slug: 'iot-line-monitoring',
    line: 'iot',
    name: '产线设备状态监测与告警',
    en: 'Line Monitoring',
    eyebrow: 'Monitoring',
    tagline: '告警要能分级，也要有人闭环',
    summary:
      '实时数据采集、阶段化监控规则、规则与设备绑定、告警记录与处理闭环，' +
      '底层用「数据管道 + 规则引擎 + 时序库」支撑。',
    overview:
      '设备状态监测最常见的失败方式不是采不到数据，而是「告警没人管」：' +
      '一有异常就推一条消息，没有分级、没有责任人、没有处理记录，' +
      '运维很快对告警脱敏。可用的监测系统要回答三件事——' +
      '什么算异常、异常了谁负责、处理完怎么留痕。',
    pains: [
      { title: '告警不分级，重要的事被淹掉', desc: '轻微波动和严重故障发同一类消息，真正需要立刻处理的反而看不见。' },
      { title: '告警发了没人管', desc: '没有责任人、没有处理状态，消息点掉就算完，问题反复出现。' },
      { title: '数据存下来了，但查不动', desc: '高频采集数据堆在关系库里，一查历史趋势就慢，最后没人用。' }
    ],
    capabilities: [
      { icon: 'activity', title: '设备数据采集', desc: '按设备采集运行数据，形成可查的实时状态与历史记录。', tags: ['实时采集', '多设备', '状态可视'] },
      { icon: 'gear', title: '阶段化监控规则', desc: '监控规则支持分阶段定义，同一设备可按运行阶段套用不同判定条件。', tags: ['阶段化规则', '条件组合', '可扩展'] },
      { icon: 'link', title: '规则与设备绑定', desc: '规则与设备、设备组建立绑定关系，避免逐台重复配置。', tags: ['规则绑定', '按组应用', '减少重复'] },
      { icon: 'bell', title: '告警记录与处理闭环', desc: '告警产生后进入处理流程，记录处理人与结果，形成可追溯的闭环。', tags: ['告警记录', '处理流程', '可追溯'] },
      { icon: 'database', title: '时序数据存储', desc: '高频采集数据进入时序库，趋势查询不拖累业务库。', tags: ['时序库', '趋势查询', '按需聚合'] },
      { icon: 'file', title: '设备日志', desc: '设备侧运行与通信日志留存，用于回溯异常发生前后的状态。', tags: ['设备日志', '异常回溯', '排查依据'] }
    ],
    how: [
      { title: '确定监测对象与判据', desc: '先明确要盯哪些设备、哪些参数、达到什么条件算异常，不追求一次全覆盖。' },
      { title: '建立监控规则', desc: '按设备或设备组配置规则，区分轻微与严重，避免所有告警同等对待。' },
      { title: '跑通告警闭环', desc: '规定告警的处理人、时限与记录要求，先在一类设备上跑顺流程。' },
      { title: '接时序存储', desc: '高频数据独立入库，保证历史趋势查询的响应。' },
      { title: '用数据回调阈值', desc: '运行一段时间后根据实际误报与漏报情况调整规则，而不是一次配死。' }
    ],
    specs: [
      { k: '规则模型', v: '监控规则支持阶段化定义，并与设备、设备组建立绑定关系' },
      { k: '判定执行', v: '由规则引擎统一执行判定，告警产生后进入处理流程' },
      { k: '告警处理', v: '告警记录含处理过程与结果，可回到具体设备与时间点' },
      { k: '存储分层', v: '高频采集数据进入时序库，业务数据留在业务库，互不拖累' },
      { k: '现场确认项', v: '监测点位、判定条件与责任人分工需结合现场确定' }
    ],
    faq: [
      { q: '和农业那套环境监测有什么区别？', a: '判断逻辑不同。农业侧看的是作物生长环境是否越界，工业侧看的是设备运行是否异常，所以工业侧的规则支持按运行阶段定义，并且更强调责任人与处理闭环。底层的接入与存储能力是同一套。' },
      { q: '我们设备上没有传感器，只有 PLC，能监测吗？', a: '可以。PLC 的寄存器里通常已经有电流、转速、报警字等状态量，通过 Modbus 读取即可，不一定需要额外加装传感器。具体能读到哪些量，取决于 PLC 程序里是否暴露了对应寄存器地址。' },
      { q: '历史数据要留多久？', a: '取决于查询需求与存储条件。高频数据占空间大，常见做法是近期保留原始精度、较早数据降采样保存。保留策略建议在实施前定清楚，避免后期数据量失控。' },
      { q: '怎么避免「告警发了没人管」？', a: '三个机制：一是告警分级，避免同等对待；二是绑定责任人；三是要求填写处理记录，形成闭环。跑一段时间看处理率和重复发生率，就知道机制有没有生效。' }
    ],
    related: ['iot-protocol-access', 'iot-maintenance']
  },

  /* ==================== 工业 · 维护保养 ==================== */
  {
    slug: 'iot-maintenance',
    line: 'iot',
    name: '设备维护保养与周期记录',
    en: 'Maintenance',
    eyebrow: 'Maintenance',
    tagline: '把抢修变成计划，把经验变成台账',
    summary:
      '维护保养记录与运行周期台账。让设备管理从「坏了才修」转向「按时保养、有据可查」。',
    overview:
      '设备维护的普遍现状是：不坏不修，坏了急修。结果是备件永远缺、停机时间不可控，' +
      '而且没人说得清这台设备历史上修过什么。把维护记录与运行周期台账建起来之后，' +
      '一是能按计划安排保养，二是出了问题能回看它的历史，' +
      '三是能据此判断哪些设备该换、哪些工序该调。',
    pains: [
      { title: '维修记录散在个人手里', desc: '谁修的、换了什么件、修了多久，只有当事师傅记得，人一走就断档。' },
      { title: '保养靠提醒，不靠计划', desc: '想起来了才保养，设备状态波动大，非计划停机多。' },
      { title: '说不清设备的运行代价', desc: '没有运行周期与维修记录，无法判断继续修还是该换。' }
    ],
    capabilities: [
      { icon: 'clipboard', title: '维护保养记录', desc: '记录每次保养与维修的时间、内容、更换件与执行人，形成设备履历。', tags: ['维修履历', '更换件记录', '执行人'] },
      { icon: 'clock', title: '运行周期台账', desc: '记录设备运行与停机周期，为保养间隔与寿命判断提供依据。', tags: ['运行周期', '停机统计', '寿命参考'] },
      { icon: 'gear', title: '计划性保养', desc: '按周期或运行时长发保养任务，从被动抢修转向计划维护。', tags: ['周期保养', '任务派发', '减少抢修'] },
      { icon: 'bell', title: '异常联动', desc: '与告警记录关联，异常处理后自动进入维护记录，避免两套台账。', tags: ['与告警联动', '记录归集', '单一台账'] },
      { icon: 'chart', title: '设备履历查询', desc: '按设备查看完整历史，支撑「修还是换」的判断。', tags: ['履历查询', '决策依据', '可导出'] },
      { icon: 'file', title: '记录留存', desc: '维护记录长期留存，用于对账、审计与经验沉淀。', tags: ['长期留存', '审计友好', '经验沉淀'] }
    ],
    how: [
      { title: '建立设备台账', desc: '先把设备清单、位置与责任人理清，一切维护记录都要挂到设备上。' },
      { title: '定义保养周期', desc: '按设备重要度与厂商建议设定保养间隔，不必所有设备同一标准。' },
      { title: '跑通记录闭环', desc: '要求每次保养与维修都填记录，先在一类关键设备上坚持。' },
      { title: '与告警打通', desc: '把异常告警的处理结果并入维护记录，避免两套台账各记一半。' },
      { title: '看数据做决策', desc: '用履历与运行周期判断设备健康度，决定是大修还是更新。' }
    ],
    specs: [
      { k: '记录对象', v: '维护保养记录与运行周期记录分别留存，均可回到具体设备' },
      { k: '关联能力', v: '可与设备数据、告警记录关联，便于回溯异常前后状态' },
      { k: '保养策略', v: '支持按周期或运行时长发保养任务，周期按设备重要性分别设定' },
      { k: '查询方式', v: '按设备查看完整履历，支撑维修与更新决策' },
      { k: '现场确认项', v: '设备清单、保养周期标准与责任人分工需现场确定' }
    ],
    faq: [
      { q: '我们连设备台账都不全，能上吗？', a: '台账是前提，建议先花时间把设备清单和责任人理清。好消息是这一步用纸质或表格做也行，系统上线后再逐步补历史数据比带着混乱的数据上线要快。' },
      { q: '师傅们不愿意填记录怎么办？', a: '这是落地第一难点。可行做法是极简：先只要求填三样——设备、做了什么、什么时候；并且把填记录和实际流程绑定（比如不填就不能领备件）。要求越少，坚持得越久。' },
      { q: '和产线监测是必须一起上吗？', a: '不是必须，但配合起来价值更大：监测负责发现异常，维护记录负责沉淀处理，两边打通后设备的完整履历才是连续的。可以先上监测，后续把记录接进来。' },
      { q: '能根据数据预测故障吗？', a: '目前这套的能力是「基于状态的告警 + 完整维护履历」，用来支撑计划性保养和历史判断。故障预测需要额外的模型与足量的失效样本，我们没有把它作为既有能力对外宣称。' }
    ],
    related: ['iot-line-monitoring', 'farm-machine-asset']
  }
]
